/** Deferred section loader. */

import { adoptStylesheets } from '@theme/section-styles';

/**
 * Lead distance for the fetch, as a share of the viewport. ~1.5 viewports is roughly
 * 450ms of warning at a hard flick scroll, against a section render measured at 40ms
 * warm and 600ms cold.
 */
const ROOT_MARGIN = '150% 0px';

const WATCH_FALLBACK_MS = 4000;
const IDLE_TIMEOUT_MS = 2000;
const INTENT_EVENTS = ['pointermove', 'pointerdown', 'touchstart', 'keydown', 'wheel', 'scroll'];
const INTENT_OPTIONS = { passive: true };
const INPUT_OPTIONS = { capture: true, passive: true };
const PENDING_SELECTOR = 'defer-section[data-defer-section]';
const REQUEST_TIMEOUT_MS = 8000;
const MAX_REQUEST_TIMEOUT_MS = 30000;
const RETRY_BASE_MS = 2000;
const MAX_RETRY_MS = 10000;
const ANCHOR_MEASURE_KEY = 'defer-section:anchor';
const EXECUTABLE_TYPES = new Set(['', 'module', 'text/javascript', 'application/javascript']);
const PRELOAD_TIMEOUT_MS = 4000;
const PRELOAD_ATTRIBUTES = ['crossorigin', 'integrity', 'referrerpolicy', 'fetchpriority'];
const canPreloadModules = document.createElement('link').relList?.supports?.('modulepreload') ?? false;

/** Running loads, keyed by placeholder. */
const tasks = new WeakMap();

/** Module preloads, keyed by absolute URL. */
const preloads = new Map();

/** Loads waiting out a retry delay. */
const sleeping = new Set();

let watching = false;
let watchTimer = 0;
let eager = false;
let eagerQueued = false;
let interacted = false;
let keyboardIntent = false;
let guarding = false;
let tabDirection = 0;
let inputCount = 0;
let reclaiming = 0;
let settle = () => {};
const settled = new Promise((resolve) => {
  settle = resolve;
});

const observer = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) start(entry.target);
    }
  },
  { rootMargin: ROOT_MARGIN }
);

function anchorId(hash) {
  if (!hash || hash.length < 2) return '';
  try {
    return decodeURIComponent(hash.slice(1));
  } catch {
    return hash.slice(1);
  }
}

function offsetOf(element) {
  return element.getBoundingClientRect().top + window.scrollY;
}

function sectionUrl(id) {
  const url = new URL(window.location.href);
  url.hash = '';
  url.searchParams.set('section_id', id);
  return url;
}

function isPermanentFailure(status, attempt) {
  if (status === 404) return attempt > 0;
  return status >= 400 && status < 500 && status !== 408 && status !== 429;
}

function retryDelay(attempt) {
  const ceiling = Math.min(RETRY_BASE_MS * 2 ** (attempt - 1), MAX_RETRY_MS);
  return ceiling * (0.5 + Math.random() / 2);
}

function sleep(task, ms) {
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => task.wake?.(), ms);
    task.wake = () => {
      window.clearTimeout(timer);
      sleeping.delete(task);
      task.wake = null;
      resolve();
    };
    sleeping.add(task);
  });
}

function cancel(task) {
  task.controller?.abort();
  task.wake?.();
}

async function download(id, task, attempt) {
  const controller = new AbortController();
  const timeout = Math.min(REQUEST_TIMEOUT_MS * 2 ** attempt, MAX_REQUEST_TIMEOUT_MS);
  const timer = window.setTimeout(() => controller.abort(), timeout);
  task.controller = controller;
  try {
    const response = await fetch(sectionUrl(id), { headers: { Accept: 'text/html' }, signal: controller.signal });
    return { status: response.status, html: response.ok ? await response.text() : '' };
  } finally {
    window.clearTimeout(timer);
    task.controller = null;
  }
}

function parseSection(html, hostId) {
  const template = document.createElement('template');
  template.innerHTML = html;
  const section = template.content.firstElementChild;
  if (section?.id !== hostId) return null;
  // A template parses with scripting off, so <noscript> fallbacks would arrive as live elements.
  for (const fallback of section.querySelectorAll('noscript')) fallback.remove();
  return section;
}

function isRunning(src, root) {
  for (const script of document.scripts) {
    if (script.src === src && !root.contains(script)) return true;
  }
  return false;
}

function preloadModule(src, inert) {
  let loading = preloads.get(src);
  if (loading) return loading;

  const link = document.createElement('link');
  link.rel = 'modulepreload';
  link.href = src;
  for (const name of PRELOAD_ATTRIBUTES) {
    if (inert.hasAttribute(name)) link.setAttribute(name, inert.getAttribute(name));
  }
  loading = new Promise((resolve) => {
    const timer = window.setTimeout(resolve, PRELOAD_TIMEOUT_MS);
    const done = () => {
      window.clearTimeout(timer);
      resolve();
    };
    link.addEventListener('load', done, { once: true });
    link.addEventListener('error', done, { once: true });
  });
  preloads.set(src, loading);
  document.head.append(link);
  return loading;
}

/**
 * Resolves once every module a parsed section will start has been fetched, so it runs as the section lands.
 *
 * @param {ParentNode} root - Parsed section that is about to be inserted.
 * @returns {Promise<void>}
 */
async function preloadModules(root) {
  if (!canPreloadModules) return;
  const loads = [];
  for (const inert of root.querySelectorAll('script[type="module"][src]')) {
    const src = new URL(inert.getAttribute('src'), document.baseURI).href;
    if (!isRunning(src, root)) loads.push(preloadModule(src, inert));
  }
  await Promise.all(loads);
}

/** Re-creates the scripts a Section Rendering response carries. */
function activateScripts(root) {
  for (const inert of root.querySelectorAll('script')) {
    const type = inert.type.trim().toLowerCase();
    if (!EXECUTABLE_TYPES.has(type)) continue;
    if (inert.src && isRunning(inert.src, root)) {
      inert.remove();
      continue;
    }

    const script = document.createElement('script');
    for (const { name, value } of inert.attributes) script.setAttribute(name, value);
    // A script-inserted classic script is async by default; the owners author `defer`.
    if (type !== 'module' && !inert.hasAttribute('async')) script.async = false;
    script.textContent = inert.textContent;
    inert.replaceWith(script);
  }
}

/**
 * Makes one attempt to swap a placeholder's section for its Section Rendering response.
 *
 * @param {HTMLElement} placeholder - Pending placeholder.
 * @param {object} task - The placeholder's running load.
 * @param {number} attempt - Zero-based attempt number.
 * @returns {Promise<boolean>} True once the placeholder has left the document, false when the attempt should be retried.
 */
async function render(placeholder, task, attempt) {
  const { status, html } = await download(placeholder.dataset.deferSection, task, attempt);
  if (!placeholder.isConnected) return true;

  const host = placeholder.closest('.shopify-section');
  if (!host || isPermanentFailure(status, attempt)) {
    placeholder.remove();
    return true;
  }

  const section = parseSection(html, host.id);
  if (!section) return false;

  await Promise.all([adoptStylesheets(section), preloadModules(section)]);
  if (!placeholder.isConnected) return true;

  host.replaceWith(section);
  activateScripts(section);
  return true;
}

async function load(placeholder, task) {
  for (let attempt = 0; ; attempt += 1) {
    if (attempt > 0) await sleep(task, retryDelay(attempt));
    if (!placeholder.isConnected) return;
    if (await render(placeholder, task, attempt).catch(() => false)) return;
  }
}

function start(placeholder) {
  const running = tasks.get(placeholder);
  if (running) {
    running.wake?.();
    return;
  }

  const task = { controller: null, wake: null, done: null };
  tasks.set(placeholder, task);
  observer.unobserve(placeholder);
  task.done = load(placeholder, task).finally(() => tasks.delete(placeholder)).then(unguard);
}

function follows(first, second) {
  return Boolean(first.compareDocumentPosition(second) & Node.DOCUMENT_POSITION_FOLLOWING);
}

function afterPaint() {
  return new Promise((resolve) => window.DieuxFrame.afterPaint(resolve));
}

function edgeStop(section, direction) {
  const { tabbableSelector, isTabbable } = window.DieuxFrame;
  const stops = Array.from(section.querySelectorAll(tabbableSelector)).filter((element) =>
    isTabbable(element, { visible: true })
  );
  return direction > 0 ? stops[0] : stops[stops.length - 1];
}

/** Moves keyboard focus into the first crossed section that lands with a stop, while focus still rests where Tab left it. */
async function reclaimFocus(crossed, target, direction) {
  const stamp = inputCount;
  const landing = target.closest('.shopify-section');
  reclaiming += 1;
  try {
    for (const placeholder of crossed) {
      const hostId = placeholder.closest('.shopify-section')?.id;
      await tasks.get(placeholder)?.done;
      await afterPaint();
      if (!isStillLanded(target, landing, stamp)) return;

      const section = hostId ? document.getElementById(hostId) : null;
      const stop = section ? edgeStop(section, direction) : null;
      if (stop) {
        stop.focus();
        return;
      }
    }
  } finally {
    reclaiming -= 1;
    unguard();
  }
}

function isStillLanded(target, landing, stamp) {
  const active = document.activeElement;
  if (active === target) return true;
  if (inputCount !== stamp || !landing?.isConnected || !(active instanceof Element)) return false;
  return active.closest('.shopify-section') === landing;
}

function notePointer() {
  inputCount += 1;
}

function noteKey(event) {
  inputCount += 1;
  if (!interacted) keyboardIntent = true;
  if (event.key !== 'Tab' || event.ctrlKey || event.metaKey) return;
  tabDirection = event.shiftKey ? -1 : 1;
  window.setTimeout(() => {
    tabDirection = 0;
  });
}

function onFocusCrossing(event) {
  const direction = tabDirection;
  if (!direction || document.documentElement.dataset.focusModality !== 'keyboard') return;

  const pending = document.querySelectorAll(PENDING_SELECTOR);
  if (!pending.length) {
    unguard();
    return;
  }

  const from = event.relatedTarget;
  const to = event.target;
  if (!(from instanceof Element) || !(to instanceof Element) || !from.isConnected) return;

  const [first, last] = direction > 0 ? [from, to] : [to, from];
  const crossed = Array.from(pending).filter((placeholder) => follows(first, placeholder) && follows(placeholder, last));
  if (!crossed.length) return;
  if (direction < 0) crossed.reverse();

  crossed.forEach(start);
  if (window.DieuxFrame) reclaimFocus(crossed, to, direction);
}

function guard() {
  if (guarding || !document.querySelector(PENDING_SELECTOR)) return;
  guarding = true;
  window.addEventListener('keydown', noteKey, INPUT_OPTIONS);
  window.addEventListener('pointerdown', notePointer, INPUT_OPTIONS);
  document.addEventListener('focusin', onFocusCrossing, true);
}

function unguard() {
  if (!guarding || reclaiming || document.querySelector(PENDING_SELECTOR)) return;
  guarding = false;
  tabDirection = 0;
  window.removeEventListener('keydown', noteKey, INPUT_OPTIONS);
  window.removeEventListener('pointerdown', notePointer, INPUT_OPTIONS);
  document.removeEventListener('focusin', onFocusCrossing, true);
}

/**
 * Brings forward every placeholder an anchor navigation is about to scroll past.
 *
 * @param {string} id - Anchor the navigation is heading to.
 * @param {number} [offset] - Scroll offset that anchor resolves to. Defaults to the anchor's own top.
 * @returns {void}
 */
function resolveAt(id, offset) {
  const target = id ? document.getElementById(id) : null;
  const goal = offset ?? (target ? offsetOf(target) : -Infinity);
  const due = Array.from(document.querySelectorAll('defer-section[data-defer-section]')).filter(
    (placeholder) => placeholder === target || offsetOf(placeholder) < goal
  );
  due.forEach(start);
}

function resolveSoon(id) {
  const task = () => resolveAt(id);
  if (window.DieuxFrame) window.DieuxFrame.measure(ANCHOR_MEASURE_KEY, task);
  else task();
}

function watch() {
  if (watching) return;
  watching = true;
  window.clearTimeout(watchTimer);
  window.removeEventListener('load', watch);
  window.removeEventListener('scroll', watch);

  for (const placeholder of document.querySelectorAll('defer-section[data-defer-section]')) {
    if (!tasks.has(placeholder)) observer.observe(placeholder);
  }
}

function whenIdle(task) {
  if ('requestIdleCallback' in window) window.requestIdleCallback(task, { timeout: IDLE_TIMEOUT_MS });
  else window.setTimeout(task, 0);
}

function startPending(placeholder) {
  if (placeholder.isConnected && !tasks.has(placeholder)) start(placeholder);
}

function loadAll() {
  eager = true;
  settle();
  document.querySelectorAll('defer-section[data-defer-section]').forEach(startPending);
}

function queueLoadAll() {
  if (eagerQueued || !interacted || document.readyState !== 'complete') return;
  eagerQueued = true;
  if (keyboardIntent) loadAll();
  else whenIdle(loadAll);
}

function onIntent() {
  interacted = true;
  for (const type of INTENT_EVENTS) window.removeEventListener(type, onIntent, INTENT_OPTIONS);
  queueLoadAll();
}

class DeferSection extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.deferSection || tasks.has(this)) return;

    guard();
    if (watching) observer.observe(this);
    if (eager) whenIdle(() => startPending(this));
    if (this.id && anchorId(window.location.hash) === this.id) resolveSoon(this.id);
  }

  disconnectedCallback() {
    observer.unobserve(this);
    const task = tasks.get(this);
    if (!task) return;

    queueMicrotask(() => {
      if (!this.isConnected) cancel(task);
    });
  }
}

if (!customElements.get('defer-section')) {
  customElements.define('defer-section', DeferSection);
  window.DieuxDeferSection = Object.freeze({ resolveAt, settled });

  window.addEventListener('online', () => {
    for (const task of sleeping) task.wake?.();
  });

  for (const type of INTENT_EVENTS) window.addEventListener(type, onIntent, INTENT_OPTIONS);

  if (document.readyState === 'complete') {
    watch();
  } else {
    window.addEventListener('load', queueLoadAll, { once: true });
    window.addEventListener('load', watch);
    window.addEventListener('scroll', watch, { passive: true });
    watchTimer = window.setTimeout(watch, WATCH_FALLBACK_MS);
  }

  const initialAnchor = anchorId(window.location.hash);
  if (initialAnchor) resolveSoon(initialAnchor);
}

/**
 * Resolves in the phase deferred sections load in: after `load`, the visitor's first input, and an idle callback.
 * @returns {Promise<void>}
 */
export function whenSettled() {
  return window.DieuxDeferSection?.settled ?? settled;
}
